/* eslint-disable react-hooks/refs */
import React, { ComponentProps, ReactNode, useEffect, useId, useRef, useState } from 'react';
import { arrow, flip, offset } from '@floating-ui/dom';
import { FloatingArrow, useFloating, useHover, useInteractions } from '@floating-ui/react';
import clsx from 'clsx';

import styles from './Tooltip.module.css';

interface TooltipProps extends ComponentProps<'div'> {
  label: ReactNode;
  placement?: 'bottom' | 'top' | 'left' | 'right';
  children: ReactNode;
  enabled?: boolean;
}

const Tooltip: React.FC<TooltipProps> = ({
  label,
  placement = 'bottom',
  children,
  enabled = true,
  className,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const arrowRef = useRef<SVGSVGElement>(null);
  const id = `${useId()}-tooltip`;

  const { refs, floatingStyles, context, update } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement,
    strategy: 'absolute',
    middleware: [
      offset(8),
      flip({ fallbackStrategy: 'initialPlacement' }),
      arrow({ element: arrowRef.current as Element }),
    ],
  });

  const hover = useHover(context, { enabled, delay: { open: 300, close: 100 } });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

  useEffect(() => {
    if (!isOpen) return;

    window.addEventListener('resize', update);

    return () => {
      window.removeEventListener('resize', update);
    };
  }, [isOpen, update]);

  return (
    <>
      <div
        ref={refs.setReference}
        {...getReferenceProps()}
        className={styles.container}
        aria-describedby={id}
      >
        {children}
      </div>
      <div
        ref={refs.setFloating}
        style={floatingStyles}
        {...getFloatingProps()}
        className={clsx(styles.tooltip, className)}
        id={id}
        role="tooltip"
        aria-current={isOpen || undefined}
        {...props}
      >
        <FloatingArrow
          ref={arrowRef}
          context={context}
          className={styles.arrow}
        />
        {label}
      </div>
    </>
  );
};

export default Tooltip;
